import React, { useMemo, useRef } from "react";

// Procedural console visualizer. Two identical bar layers — a muted "base" and
// an amber "fill" clipped to the played portion — so the played/unplayed split
// tracks progress exactly. Bars animate only while playing; heights are a stable
// per-bar profile (not the real audio signal — see the CORS note in the spec).
const BAR_COUNT = 56;

function buildBars() {
  const bars = [];
  for (let i = 0; i < BAR_COUNT; i++) {
    // deterministic pseudo-random, so the profile is stable across renders
    const seed = Math.sin(i * 12.9898) * 43758.5453;
    const r = seed - Math.floor(seed);
    bars.push({
      h: 22 + r * 66,               // resting height %
      d: 620 + ((i * 53) % 460),    // animation duration ms
      delay: (i * 41) % 720,        // phase offset ms
    });
  }
  return bars;
}

function Waveform({ progress = 0, playing = false, segments = [], onSeek }) {
  const wrapRef = useRef();
  const bars = useMemo(buildBars, []);

  const seekFromEvent = (clientX) => {
    if (!onSeek || !wrapRef.current) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const pct = ((clientX - rect.left) / rect.width) * 100;
    onSeek(Math.min(100, Math.max(0, pct)));
  };

  const renderBars = (cls) => (
    <div className={"demo-wave-bars " + cls} aria-hidden="true">
      {bars.map((b, i) => (
        <i key={i} style={{ "--h": b.h + "%", "--d": b.d + "ms", "--delay": "-" + b.delay + "ms" }} />
      ))}
    </div>
  );

  return (
    <div
      className="demo-wave"
      ref={wrapRef}
      data-playing={playing ? "1" : "0"}
      style={{ "--wave-progress": progress + "%" }}
      onClick={(e) => seekFromEvent(e.clientX)}
      role="slider"
      aria-label="Seek within call"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(progress)}
      tabIndex={onSeek ? 0 : -1}
      onKeyDown={(e) => {
        if (!onSeek) return;
        if (e.key === "ArrowRight") onSeek(Math.min(100, progress + 5));
        if (e.key === "ArrowLeft") onSeek(Math.max(0, progress - 5));
      }}
    >
      {renderBars("demo-wave-base")}
      {renderBars("demo-wave-fill")}
      {segments.map((pos, i) => (
        <span key={i} className="demo-wave-tick" style={{ left: pos + "%" }} />
      ))}
      <span className="demo-wave-head" style={{ left: progress + "%" }} />
    </div>
  );
}

export default Waveform;
